import { Encoding, EncodingResult, FilterResult, PivotResult, Model as VegasModel } from '@mode-vegas/vegas';
import {
  AccountResource,
  NotebookVisualizationResource,
  PdfExportResource,
  QueryResource,
  QueryRunResource,
  QueryRunViewResource,
  ReportResource,
  ReportRunResource,
  SpaceResource,
  StarResource,
  TableResource,
} from '@mode/legacy/types-hal-resource';
import { Map, OrderedSet } from 'immutable';

import { ChartTypes, FlamingoTypes, RMap, StateRecord, VizTypes } from '@mode/shared/contract-common';
import { DataViewResponseTypes, ReportDomain } from '@mode/shared/contract-common';
import { NotebookRunState } from './client/notebooks.state';
import { ImmutableRecordSet } from './records';

export const BLANK_HISTORY: Readonly<ChartTypes.ChartHistory> = {
  undoStack: [],
  last: null,
  redoStack: [],
};

export interface NormalizedNotebookVizArray {
  runState: NotebookRunState;
  tokens: OrderedSet<string>;
  cellTokens: Map<string, string>;
  cellRunTokens: Map<string, string>;
  titles: Map<string, string>;
  frozen: OrderedSet<string>;
  resources: Map<string, NotebookVisualizationResource>;
}

export interface ChartStateValidations {
  isCompleteEncoding: boolean;

  canSort: boolean;
  canSwap: boolean;
}

export interface ChartState extends ChartTypes.CoreChart, ChartStateValidations {
  encResult: EncodingResult;

  canRedo: boolean;
  canUndo: boolean;
  undoDesc: string;
  redoDesc: string;
  canGoUp: boolean;
  canGoDown: boolean;
}

export interface TableState extends StateRecord {
  queryToken: string;
  vizType: VizTypes.Table;
  resource: TableResource;
  currentView: any; // Needs an interface
  history: any[];
}

export interface QueryState extends StateRecord {
  resource: QueryResource;
  name: string;
  raw_query: string;
  data_source_id: string;
  limit: boolean;
  last_run_id: string;
  formatted: boolean;
  explorations_count: number;
  report_imports_count: number;
  original_query?: string;
  formatted_query?: string;
  dbt_metric_id?: number;
  dbt_metric_token?: string;
  charts?: ReadonlyArray<ChartTypes.ChartResource>;
}

export interface RecentReportRun {
  lastRunState: string;
  lastReportRunLink: string;
  lastReportRunId: string;
}

export interface ReportRunState extends StateRecord {
  resource: ReportRunResource;
  running: boolean;
  loading: boolean;
  canceling: boolean;
  state: string;
  share: string;
  localCreatedAt?: Date;
  createdAt?: Date;
  errorCause?: ReportDomain.ReportRunFailCause;
}

export interface ModelState extends StateRecord {
  // todo(kevin): figure out how to remove this or swap it out with
  // a model that doesn't include the jungle
  model: VegasModel;

  state: string;
  token: string; // query token since datasets are usually accessed by query token and not result or run token
  underBrowserSizeLimit: boolean;
  csv: string;
  json: string;
  oversized: boolean;
  error_message?: string;

  content: any[];
  columns: any[];
  count: number;
  bytes: number;

  // This is used for filtered dataset building
  queryName: string;
  reportDataUrl: string;
  reportQueryUrl: string;
  content_length: number;
}

export interface ViewDataModalResult {
  // todo(kevin): figure out how to remove this or swap it out with
  // a model that doesn't include the jungle
  model: VegasModel;

  encoding: Encoding;
  format: ChartTypes.ChartFormat;
  summary: PivotResult;
  complete: FilterResult;
}

export interface QueryRunState extends StateRecord {
  query_token: string;
  datasourceToken: string;
  resource: QueryRunResource;
  rawSource: string; // shows liquid template code
  renderedSource: string;
  state: string;
  running: boolean;
  warning: boolean;
  runQueued: boolean;
  cancelQueued: boolean;
  timeCompleted: string;
  reportDataUrl: string;
  reportQueryUrl: string;
  reportRunUrl: string;
  limit: boolean;
  createdAt: string; // created at according to database
  localCreatedAt?: Date; // time when the reducer created this run

  errorCode?: string;
  errorMessage: string;
  errorLineHtml: string;

  // bytes and content_length are here because there's currently
  // nowhere to store query run result information in the state
  bytes?: number;
  content_length?: number;

  isDbResultOversized: boolean;
  errorText?: string;
  errorResolutionText?: string;
  resolutionLink?: string;
}

export interface CalcEditorModalState {
  readonly isOpen: boolean;
  readonly viewName: string;
  readonly switchViewToken?: string;
  readonly calc?: FlamingoTypes.ModeCalc;
  readonly viewMode?: 'static' | 'edit';
  readonly readOnly?: boolean;
  readonly duplicate?: boolean;
  readonly isImportedDataset?: boolean;
  readonly canShowFieldDescription?: boolean;
}

// For supporting the editor in the legacy code
export interface EditorSelectedQuery {
  name: string;
  token: string;
  last_run_id: string;
  storedTables: ReadonlyArray<TableResource>;
  storedCharts: ReadonlyArray<ChartTypes.ChartResource>;
}

export interface SwitchAttrsState {
  name: string; // querytoken
  from: string; // queryRunToken
  columns: FlamingoTypes.ModeAttr[];
  calcs: FlamingoTypes.ModeAttr[];
  resources: RMap<DataViewResponseTypes.QueryRunViewAttrResource>;
}

export interface SelectedReportState {
  model: FlamingoTypes.ModeModel;
  chartsByQuery: Map<string, OrderedSet<string>>;
  chartModels: ImmutableRecordSet<Readonly<ModelState>>;
  columnsByQuery: Map<string, FlamingoTypes.RecordsResult['columns']>; // stories the flamingo schema for a query token
  creator: AccountResource;
  pollingReportRun: boolean;
  queries: ImmutableRecordSet<Readonly<QueryState>>;
  queryRuns: ImmutableRecordSet<Readonly<QueryRunState>>;
  queryViewResourceByQuery: RMap<QueryRunViewResource>;
  report: ReportResource;
  theme: {
    name: string;
    token: string;
    href: string;
    customSource: string;
  };
  reportIsNew: boolean;
  reportDisplayName: string;
  reportLayout: string;
  renderedReportLayout: string;
  reportRun: ReportRunState;
  reportRunPdfExport: PdfExportResource;
  reportSpace: SpaceResource;
  switchAttrsByQuery: RMap<SwitchAttrsState>;
  tables: ImmutableRecordSet<Readonly<TableState>>;
  tablesByQuery: Map<string, OrderedSet<string>>;
  userStar: StarResource;
  calcEditorModal: CalcEditorModalState;

  // Notebook state specific to report view iframe
  notebookViz: NormalizedNotebookVizArray;

  // Added to check for recent runs when user is on
  // older runs. This is used for showing banner
  hasRecentReportRun: boolean;
  recentReportRun?: RecentReportRun | null;
}
